import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import {
  DEFAULT_ACTIVE_CONTEXT,
  getActiveContext,
  hasStoredActiveContext,
  setActiveContext,
  subscribeActiveContext,
} from '@kiao-voice/api-client'
import type { ActiveContextWriteReason, SwitchableContext } from '@kiao-voice/api-client'

export interface UseOrganizationIdentityArgs {
  /** Only fetch when the user is authenticated. */
  enabled: boolean
  /**
   * Loads the signed-in user's switchable contexts. Injected per app — desktop
   * binds its api-client (`getOrganizationContexts`), web binds its token fetch.
   * REQUIRED: the shared hook is transport-agnostic, so each consumer supplies
   * the call (mirrors `useVoiceQuota`'s injected `fetchUsage`).
   */
  loadContexts: () => Promise<{ contexts: SwitchableContext[] }>
  /**
   * When true, the server smart default (`isDefault`) is PERSISTED to the shared
   * active-context store on first load, so the store, the request headers, and
   * the server all agree. Both app-level consumers — desktop's binding and web's
   * app shell — set it `true`: without the write, a child surface that reads the
   * store directly scopes to the bare `personal` fallback while the server is
   * acting in the default team (e.g. /account showing the team credit balance
   * with personal top-up controls).
   *
   * Default `false` = DISPLAY-ONLY: the default is highlighted, never written.
   * Web's team page relies on that branch — it renders from the resolved context
   * while the app shell owns the single adoption write, and a second writer
   * would race it. The flag is therefore load-bearing in BOTH positions.
   */
  adoptDefaultToStore?: boolean
  /**
   * Whose list this is. Passed only by the callers that gate a disabled
   * workspace on the list: web's app shell (the stored access token) and
   * desktop (the user id). With it, a failed read keeps the list read for this
   * same identity, so a workspace the list closed stays closed; a list read for
   * another identity is cleared. A change drops any read still in flight and
   * reads again, the last list staying on screen until that read answers.
   * Omitted: a failed read clears the list.
   */
  identityKey?: string | number | null
  /** Test seam — defaults to the shared active-context store getter. */
  readActiveContext?: () => string
  /**
   * Test seam — whether the user has EXPLICITLY chosen a context. When false
   * (first load, nothing stored) the client sends no `X-Active-Context` header,
   * so the SERVER picks the smart default; this hook then highlights that same
   * default (the `isDefault` context) instead of the bare personal fallback.
   * Defaults to the shared store predicate.
   */
  hasStored?: () => boolean
  /**
   * Test seam — defaults to the shared active-context store setter. `options`
   * carries the notification reason: adoption passes `{ reason: 'adopt' }` so
   * context-scoped subscribers revalidate instead of discarding data, while a
   * user-driven switch omits it (`'set'`).
   */
  writeActiveContext?: (key: string, options?: { reason?: ActiveContextWriteReason }) => void
  /** Test seam — defaults to the shared active-context subscription. */
  subscribe?: (listener: (key: string) => void) => () => void
}

interface LoadedContexts {
  identityKey: string | number | null | undefined
  contexts: SwitchableContext[]
}

const NO_CONTEXTS: SwitchableContext[] = []

/** The same state when nothing changed, so an unchanged answer does not re-render. */
function loadedFor(
  current: LoadedContexts,
  identityKey: LoadedContexts['identityKey'],
  contexts: SwitchableContext[]
): LoadedContexts {
  return current.identityKey === identityKey && current.contexts === contexts
    ? current
    : { identityKey, contexts }
}

export interface OrganizationIdentity {
  /** All switchable contexts for the signed-in user (empty until loaded). */
  contexts: SwitchableContext[]
  /** The key of the currently-active context (`'personal'` or `org:<id>`). */
  activeKey: string
  /**
   * The currently-active context, resolved from {@link contexts} by
   * {@link activeKey}. `undefined` until contexts load, or when the active key
   * no longer maps to an available context (e.g. a seat was revoked, so the
   * server stopped returning it) — the caller then falls back to the personal /
   * email-only layout instead of rendering a stale membership.
   */
  active?: SwitchableContext
  /** Active context org name, or undefined when no context is resolved. */
  orgName?: string
  /** Active context mode — `'personal'` or `'team'`. */
  mode?: SwitchableContext['mode']
  /** The user's role in the active context. */
  role?: SwitchableContext['role']
  /** Human-readable plan label for the active context (e.g. `'Team'`). */
  planLabel?: string
  /**
   * Whether the caller may use the active context. A `'disabled'` team stays
   * listed and selectable; its content is closed until an admin enables the
   * caller again. Undefined when no context is resolved or the API predates it.
   */
  accessState?: SwitchableContext['accessState']
  /** Whether the active context is an org in team mode (not personal). */
  isTeamContext: boolean
  /** Switch the active context: persist the key and refetch the contexts. */
  selectContext: (key: string) => void
  /**
   * Refetch the contexts list (e.g. after a membership change). Settles once
   * this fetch is done, its contexts applied or the failure recorded; it never
   * rejects, so callers may ignore it or wait on it (a "checking…" button).
   */
  refetch: () => Promise<void>
}

/**
 * Drives the account zone from the ACTIVE CONTEXT (Claude-style per-context
 * switching). Loads the signed-in user's switchable contexts via the injected
 * {@link UseOrganizationIdentityArgs.loadContexts}, resolves the currently-active
 * one via the shared active-context store, and re-reads whenever the user
 * switches. Shared by desktop + web; lives in `@kiao-voice/hooks` so React never
 * enters the backend dependency graph.
 *
 * The server only returns contexts the caller may act in (the personal owned
 * org is always present; an external-team context appears only for an ACTIVE
 * seat — a revoked / pending seat is omitted). So a context present in the list
 * is by definition an active membership: we never render a non-active seat as a
 * member because it simply isn't in the list. If the persisted active key points
 * at a context the server no longer returns, {@link OrganizationIdentity.active}
 * resolves to `undefined` and the account zone degrades to the email-only
 * layout. A failed fetch is reported (console.warn). With an `identityKey` it
 * keeps the last list read for that identity: being offline or timing out is no
 * answer, so it must not reopen a workspace the last answer closed. Without
 * one, it clears the list. Before any answer the list is empty.
 */
export function useOrganizationIdentity({
  enabled,
  loadContexts,
  adoptDefaultToStore = false,
  identityKey,
  readActiveContext = getActiveContext,
  hasStored = hasStoredActiveContext,
  writeActiveContext = setActiveContext,
  subscribe = subscribeActiveContext,
}: UseOrganizationIdentityArgs): OrganizationIdentity {
  // The list and the identity it was read for.
  const [loaded, setLoaded] = useState<LoadedContexts>(() => ({
    identityKey,
    contexts: NO_CONTEXTS,
  }))
  const contexts = loaded.contexts
  const [activeKey, setActiveKey] = useState<string>(() => readActiveContext())
  // Sequence guard: a rapid switch can leave an older fetch in flight; only the
  // latest request is allowed to write, so a slow earlier response can't clobber
  // the newer contexts.
  const requestSeqRef = useRef(0)
  // The committed identity: a refetch kept from an earlier identity (a handler
  // captured before the switch) neither reads nor writes. Updated on commit, so
  // a render that is discarded never moves it.
  const currentIdentityRef = useRef(identityKey)
  useLayoutEffect(() => {
    currentIdentityRef.current = identityKey
  }, [identityKey])

  const refetch = useCallback(async () => {
    if (currentIdentityRef.current !== identityKey) return
    const seq = (requestSeqRef.current += 1)
    const isLatest = () =>
      seq === requestSeqRef.current && currentIdentityRef.current === identityKey

    try {
      const { contexts: nextContexts } = await loadContexts()
      if (isLatest()) {
        setLoaded((current) => loadedFor(current, identityKey, nextContexts))
      }
    } catch (cause) {
      if (!isLatest()) return
      // No answer: a caller tracking the identity keeps the last list read for
      // it, so a workspace it closed stays closed; otherwise the list is dropped.
      // Logged so a broken contexts fetch is diagnosable.
      console.warn('account: failed to load organization contexts', cause)
      setLoaded((current) =>
        identityKey !== undefined && current.identityKey === identityKey
          ? current
          : loadedFor(current, identityKey, NO_CONTEXTS)
      )
    }
  }, [loadContexts, identityKey])

  // Load contexts when enabled, and keep the active key in sync with the shared
  // store (so a switch from anywhere — e.g. another component — re-renders here).
  useEffect(() => {
    if (!enabled) {
      // Invalidate any in-flight fetch so a late response can't repopulate the
      // cleared contexts after sign-out.
      requestSeqRef.current += 1
      setLoaded((current) => loadedFor(current, identityKey, NO_CONTEXTS))
      setActiveKey(readActiveContext())
      return
    }

    setActiveKey(readActiveContext())
    void refetch()
    const unsubscribe = subscribe((key) => {
      setActiveKey(key)
      // A context switch changes which org's data is authoritative; refetch so
      // the plan label / role for the newly-active context is fresh. The
      // notification reason is intentionally ignored: the adoption write below
      // lands here too and costs one refetch of the very list that produced the
      // adopted key — an accepted price for keeping one code path.
      void refetch()
    })

    return () => {
      // Invalidate any in-flight fetch so it can't write after unmount.
      requestSeqRef.current += 1
      unsubscribe()
    }
  }, [enabled, identityKey, refetch, readActiveContext, subscribe])

  // Opt-in: persist the server smart default to the shared store on first load.
  // Guarded by `hasStored()` + a value check so it writes at most once and can't
  // loop (the write notifies the subscription above → one refetch, after which
  // `hasStored()` is true and this is a no-op). Display-only consumers leave
  // `adoptDefaultToStore` off and never reach this.
  //
  // `reason: 'adopt'` marks the write as "spell out the context the server was
  // already serving", not a workspace switch, so context-scoped subscribers
  // revalidate in place instead of blanking still-valid data.
  //
  // The `fallback !== readActiveContext()` guard is deliberate, not just a
  // no-op saver: for a user whose default IS personal it leaves the store
  // UNSET. An explicit `personal` is honoured by the server forever, which
  // would freeze their landing workspace as personal even after they gain a
  // paid seat — leaving it unset keeps the server free to re-resolve.
  useEffect(() => {
    if (!adoptDefaultToStore || !enabled || hasStored()) return
    const fallback = contexts.find((context) => context.isDefault)?.key
    if (fallback && fallback !== readActiveContext()) {
      writeActiveContext(fallback, { reason: 'adopt' })
    }
  }, [adoptDefaultToStore, enabled, contexts, hasStored, readActiveContext, writeActiveContext])

  // When the user hasn't explicitly chosen a context, the client omits the
  // `X-Active-Context` header and the SERVER acts in its smart default — so
  // highlight that same default context (`isDefault`) rather than the bare
  // personal fallback, keeping the switcher in lockstep with what the server is
  // actually doing. This is DISPLAY resolution: the moment the user switches (or
  // `adoptDefaultToStore` persists the default), `hasStored()` flips true and
  // their explicit `activeKey` wins.
  const defaultContext = contexts.find((context) => context.isDefault)
  const effectiveKey = hasStored() ? activeKey : (defaultContext?.key ?? activeKey)

  const active =
    contexts.find((context) => context.key === effectiveKey) ??
    // Fall back to the personal context if the resolved key is unknown but the
    // personal context is present — never render a stale/absent membership.
    contexts.find((context) => context.key === DEFAULT_ACTIVE_CONTEXT)

  const selectContext = useCallback(
    (key: string) => {
      // Setting the shared store notifies the subscription above, which updates
      // activeKey and refetches — so the switcher stays the single source of truth.
      writeActiveContext(key)
    },
    [writeActiveContext]
  )

  return {
    contexts,
    activeKey: effectiveKey,
    active,
    orgName: active?.name,
    mode: active?.mode,
    role: active?.role,
    planLabel: active?.planLabel,
    accessState: active?.accessState,
    isTeamContext: active?.mode === 'team',
    selectContext,
    refetch,
  }
}
